import React, { useEffect, useRef } from 'react';
import { Check, Loader2, Sparkles, TriangleAlert } from 'lucide-react';
import useProductFetchProgress, { FetchStage } from '../../../hooks/useProductFetchProgress';

interface KnowledgeFetchProgressProps {
  productId: number;
  /** modal = 登録ウィザード内 / panel = 一覧右側の取得中画面 */
  variant?: 'modal' | 'panel';
  /** 主要ジョブ完了（done）に切り替わった時に一度だけ呼ばれる。 */
  onDone?: () => void;
  enabled?: boolean;
}

const PRIMARY = '#616FC9';
const SUCCESS = '#44C2A4';

const StageRow: React.FC<{ stage: FetchStage; isCurrent: boolean }> = ({ stage, isCurrent }) => {
  const done = stage.status === 'completed';
  const failed = stage.status === 'failed';
  const warning = stage.status === 'completed_with_warning';

  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span
        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
        style={{
          background: done ? 'rgba(68,194,164,.14)' : failed || warning ? 'rgba(201,160,97,.16)' : 'rgba(97,111,201,.10)',
          color: done ? SUCCESS : failed || warning ? '#A87C2B' : PRIMARY,
        }}
      >
        {done ? (
          <Check className="h-3.5 w-3.5" />
        ) : failed || warning ? (
          <TriangleAlert className="h-3.5 w-3.5" />
        ) : isCurrent ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        )}
      </span>
      <span
        className={`text-[13px] ${
          done ? 'font-semibold text-dark' : isCurrent ? 'font-semibold text-primary' : 'text-gray-400'
        }`}
      >
        {stage.label}
        {failed && <span className="ml-1 text-[11px] text-[#A87C2B]">（スキップ）</span>}
        {warning && <span className="ml-1 text-[11px] text-[#A87C2B]">（一部不足）</span>}
      </span>
    </div>
  );
};

/**
 * Knowledge 商品の情報取得進捗を実 API でポーリングして表示する共有コンポーネント。
 * ウィザードの Deep Research（④）と一覧右側の取得中画面（⑥）で共用する。
 */
const KnowledgeFetchProgress: React.FC<KnowledgeFetchProgressProps> = ({
  productId,
  variant = 'panel',
  onDone,
  enabled = true,
}) => {
  const { percent, stages, done, status } = useProductFetchProgress(productId, {
    enabled,
  });

  const doneFiredRef = useRef(false);
  useEffect(() => {
    if (done && !doneFiredRef.current) {
      doneFiredRef.current = true;
      onDone?.();
    }
  }, [done, onDone]);

  // 進行中の段階（最初の未完了ステージ）にスピナーを出す。
  const currentIndex = stages.findIndex((stage) => stage.status === 'pending');

  const ringSize = variant === 'modal' ? 148 : 120;
  const displayPercent = done ? 100 : percent;
  const hasWarnings = status === 5 || status === '5' || status === 'failed' ||
    stages.some((stage) => stage.status === 'completed_with_warning' || stage.status === 'failed');
  const completedColor = hasWarnings ? '#A87C2B' : SUCCESS;

  return (
    <div className={`flex flex-col items-center ${variant === 'panel' ? 'px-6 py-10' : 'py-2'}`}>
      <div
        className="relative grid place-items-center rounded-full"
        style={{
          width: ringSize,
          height: ringSize,
          background: `conic-gradient(${done ? completedColor : PRIMARY} ${displayPercent * 3.6}deg, #ECEAE3 0)`,
        }}
      >
        <div
          className="grid place-items-center rounded-full bg-white"
          style={{ width: ringSize - 22, height: ringSize - 22, boxShadow: 'inset 0 0 0 1px #E8E6E0' }}
        >
          <Sparkles className="h-6 w-6" style={{ color: done ? completedColor : PRIMARY }} />
          <span className="mt-0.5 text-[15px] font-extrabold" style={{ color: done ? completedColor : PRIMARY }}>
            {displayPercent}%
          </span>
        </div>
      </div>

      <h3 className="mt-5 text-center text-[15px] font-bold text-dark">
        {done ? hasWarnings ? '取得が終了しました（一部不足）' : '情報の取得が完了しました' : 'Tasky Deep Research で情報を収集中'}
      </h3>
      <p className="mt-1 max-w-[420px] text-center text-[12px] leading-relaxed text-gray-500">
        {done
          ? hasWarnings ? '取得できなかった情報や画像があります。商品情報を確認し、不足する素材を追加してください。' : '基本情報・戦略マップ・ブランド・商品画像を取得しました。'
          : '公式サイトを解析して、戦略に必要な情報を自動で収集しています。'}
      </p>

      <div className="mt-5 w-full max-w-[360px] rounded-2xl border border-[#E8E6E0] bg-white px-5 py-3">
        {stages.map((stage, index) => (
          <StageRow key={stage.key} stage={stage} isCurrent={!done && index === currentIndex} />
        ))}
      </div>
    </div>
  );
};

export default KnowledgeFetchProgress;
